import api from './authService';

/**
 * S3 Upload Service for direct browser uploads
 * This service handles client-side uploads to S3 using presigned URLs
 */

/**
 * Upload a file directly to S3 using presigned URL
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'blog-uploads')
 * @param {function} onProgress - Progress callback function
 * @returns {Promise<string>} - Returns the S3 file URL
 */
export const uploadToS3 = async (file, folder = 'blog-uploads', onProgress = null, options = {}) => {
  try {
    console.log('🚀 Starting S3 upload for:', file.name);

    // Step 1: Get presigned URL from backend
    const endpoint = options.endpoint || '/blog/s3-presigned-url';
    const payload = {
      fileName: file.name,
      fileType: file.type,
      folder: folder,
      ...(options.extraData || {})
    };
    const presignedResponse = await api.post(endpoint, payload, {
      headers: options.headers
    });

    const { uploadUrl, fileUrl, key } = presignedResponse.data;
    console.log('✅ Got presigned URL:', { uploadUrl, fileUrl, key });

    // Step 2: Upload file directly to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
    }

    console.log('✅ File uploaded successfully to S3:', fileUrl);
    return fileUrl;

  } catch (error) {
    console.error('❌ S3 upload error:', error);
    
    // Check if S3 is not available and we should use local upload
    if (error.response?.data?.useLocalUpload) {
      console.log('🔄 S3 not available, falling back to local upload');
      throw new Error('S3_NOT_AVAILABLE');
    }
    
    throw error;
  }
};

/**
 * Upload with progress tracking using XMLHttpRequest
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'blog-uploads')
 * @param {function} onProgress - Progress callback function (receives percentage)
 * @returns {Promise<string>} - Returns the S3 file URL
 */
export const uploadToS3WithProgress = async (file, folder = 'blog-uploads', onProgress = null, options = {}) => {
  try {
    console.log('🚀 Starting S3 upload with progress for:', file.name);

    // Step 1: Get presigned URL from backend
    const endpoint = options.endpoint || '/blog/s3-presigned-url';
    const payload = {
      fileName: file.name,
      fileType: file.type,
      folder: folder,
      ...(options.extraData || {})
    };
    const presignedResponse = await api.post(endpoint, payload, {
      headers: options.headers
    });

    const { uploadUrl, fileUrl, key } = presignedResponse.data;
    console.log('✅ Got presigned URL:', { uploadUrl, fileUrl, key });

    // Step 2: Upload file directly to S3 with progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          console.log('✅ File uploaded successfully to S3 with progress:', fileUrl);
          resolve(fileUrl);
        } else {
          reject(new Error(`S3 upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        // Check if it's a CORS error
        const errorMessage = xhr.status === 0 ? 'CORS_ERROR' : `S3 upload failed: ${xhr.statusText}`;
        reject(new Error(errorMessage));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('S3 upload aborted'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });

  } catch (error) {
    console.error('❌ S3 upload with progress error:', error);
    
    // Check if it's a CORS error or network error
    if (error.message === 'CORS_ERROR' || error.message.includes('CORS') || error.message.includes('Network error')) {
      console.log('🔄 CORS error detected, falling back to server-side upload');
      throw new Error('CORS_ERROR');
    }
    
    // Check if S3 is not available and we should use local upload
    if (error.response?.data?.useLocalUpload) {
      console.log('🔄 S3 not available, falling back to local upload');
      throw new Error('S3_NOT_AVAILABLE');
    }
    
    throw error;
  }
};

/**
 * Fallback to server-side upload when S3 direct upload fails (e.g., CORS)
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'blog-uploads')
 * @param {function} onProgress - Progress callback function
 * @param {object} options - Additional options (endpoint, headers, etc.)
 * @returns {Promise<string>} - Returns the S3 file URL
 */
export const fallbackToServerUpload = async (file, folder = 'blog-uploads', onProgress = null, options = {}) => {
  try {
    console.log('🔄 Using server-side upload fallback for:', file.name);
    
    const formData = new FormData();
    formData.append('files', file);
    formData.append('folder', folder);

    const endpoint = options.endpoint || '/blog/upload/guest';
    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(options.headers || {})
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        if (progressEvent.total) {
          const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentComplete);
        }
      } : undefined
    });

    if (response.data.attachments && response.data.attachments.length > 0) {
      return response.data.attachments[0].fileUrl;
    } else {
      throw new Error('No file URL returned from server upload');
    }
  } catch (error) {
    console.error('❌ Server upload fallback failed:', error);
    throw error;
  }
};
